import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products, testimonials } from '../../mocks/products';
import { useCart } from '../../hooks/useCart';

export default function HomePage() {
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const featuredProducts = products.slice(0, 6);

  useEffect(() => {
    document.title = 'PEL Partner Company | Premium Radiators Belgium & UK';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'PEL Partner Company — Belgium\'s premium radiator specialist. Shop designer flat panel radiators, towel warmers & heating systems. Serving Belgium, UK & Europe. Tienen, Belgium.');

    const schemaId = 'schema-home';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'PEL Partner Company',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          logo: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
          description: "Belgium's premium radiator specialist supplying designer flat panel radiators, towel warmers and heating systems across Belgium, UK and Europe.",
          telephone: '+32487261970',
          email: 'pelpartnercompany@gmail.com',
          address: [
            {
              '@type': 'PostalAddress',
              streetAddress: 'Leuvenselaan 467',
              addressLocality: 'Tienen',
              postalCode: '3300',
              addressCountry: 'BE',
            },
            {
              '@type': 'PostalAddress',
              addressLocality: 'London',
              addressCountry: 'GB',
            },
          ],
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 50.8027,
            longitude: 4.9408,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
          ],
          sameAs: [],
          priceRange: '$$',
          areaServed: ['BE', 'GB', 'EU'],
          currenciesAccepted: 'EUR',
          paymentAccepted: 'Visa, Mastercard, Google Pay, Bank Transfer',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'PEL Partner Company',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'PEL Partner Company',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          logo: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+32487261970',
              contactType: 'customer service',
              email: 'pelpartnercompany@gmail.com',
              areaServed: ['BE', 'GB', 'EU'],
              availableLanguage: ['English', 'Dutch', 'French'],
            },
          ],
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Leuvenselaan 467',
            addressLocality: 'Tienen',
            postalCode: '3300',
            addressCountry: 'BE',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Where does PEL Partner Company deliver radiators?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'PEL Partner Company delivers premium radiators across Belgium, the UK, and the wider European Union. We ship from our Belgium office in Tienen and our UK office in London.',
              },
            },
            {
              '@type': 'Question',
              name: 'What types of radiators does PEL Partner Company sell?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We supply a wide range of heating products including flat panel designer radiators, horizontal tube radiators, towel warmers (electric and hydronic), and radiator valves. All products are premium European quality.',
              },
            },
            {
              '@type': 'Question',
              name: 'What payment methods are accepted?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We accept Visa, Mastercard, Google Pay, and direct bank transfer (IBAN/SWIFT). All card payments are secured with 256-bit SSL encryption and are PCI DSS compliant.',
              },
            },
            {
              '@type': 'Question',
              name: 'How long does delivery take for radiators in Belgium?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Standard delivery within Belgium takes 3–5 business days. Express shipping options are available at checkout. Orders over €100 qualify for free shipping.',
              },
            },
          ],
        },
      ]);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar cartItemCount={getCartCount()} />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <video
          src="https://public.readdy.ai/ai/video_res/fad413af-1854-45ea-927d-6f7d8f30635e.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 flex flex-col md:flex-row items-center justify-between">
          <div className="text-white mb-8 md:mb-0">
            <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-4 font-medium">Belgium&apos;s Premium Heating Specialist</p>
            <h1 className="font-serif text-7xl md:text-8xl font-bold leading-tight mb-4">
              Warmth,<br />Redefined.
            </h1>
          </div>
          
          <div className="text-white text-right">
            <p className="text-xl md:text-2xl font-light leading-relaxed mb-8 max-w-md">
              Premium European radiators<br />
              for every space — delivered<br />
              across Belgium &amp; beyond
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-sm font-medium hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              Explore Radiators
              <span className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-sm"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="bg-[#F5F1E8] py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="font-serif text-5xl font-bold text-center mb-16 text-black">
            Featured Collection
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative overflow-hidden aspect-square">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.status && (
                    <span className="absolute top-4 left-4 bg-black text-white px-4 py-1 rounded-full text-xs font-medium">
                      {product.status}
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-black">{product.name}</h3>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-yellow-500 text-sm`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">({product.reviews})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {product.originalPrice && (
                      <span className="text-gray-400 line-through text-sm">
                        ${product.originalPrice.toFixed(2)}
                      </span>
                    )}
                    <span className="text-2xl font-bold text-black">
                      ${product.price.toFixed(2)}
                    </span>
                  </div>
                  <button className="mt-4 w-full bg-black text-white py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap">
                    View Details
                  </button>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-block bg-black text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Images side */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/063322b5a42e0523a3bd4ca20241bb5b.jpg"
                  alt="Premium Radiators"
                  className="w-full h-[320px] object-cover rounded-2xl"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=elegant%20anthracite%20designer%20radiator%20in%20luxury%20bathroom%20interior%2C%20modern%20European%20style%20towel%20warmer%2C%20warm%20tones%2C%20sophisticated%20bathroom%20setting%20with%20heated%20towel%20rail%2C%20premium%20heating%20product%20photography&width=400&height=500&seq=rad2&orientation=portrait"
                  alt="Designer Heating"
                  className="w-full h-[320px] object-cover rounded-2xl mt-10"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black text-white px-8 py-4 rounded-2xl text-center whitespace-nowrap">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Years of Experience</p>
              </div>
            </div>

            {/* Text side */}
            <div className="lg:pl-8 pt-8 lg:pt-0">
              <span className="inline-block bg-[#F5F1E8] text-black px-4 py-2 rounded-full text-xs font-medium uppercase tracking-wider mb-6">
                About Us
              </span>
              <h2 className="font-serif text-5xl font-bold text-black leading-tight mb-6">
                Your Trusted Partner<br />for Premium Heating
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                PEL Partner Company is a Belgium-based specialist in high-quality radiators and heating solutions. We source and supply premium European radiators — from flat panel designs to towel rails and column models — built to last and designed to impress.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-10">
                Whether you're outfitting a single room or an entire building, we offer a wide range of sizes, styles, and models to match every project. Our team is committed to delivering the best products at competitive prices, with reliable stock and fast service.
              </p>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-6 mb-10">
                <div className="text-center border-r border-gray-200 last:border-0">
                  <p className="text-3xl font-bold text-black">500+</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Products</p>
                </div>
                <div className="text-center border-r border-gray-200 last:border-0">
                  <p className="text-3xl font-bold text-black">1000+</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Happy Clients</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-black">🇧🇪</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Based in Belgium</p>
                </div>
              </div>

              {/* Features list */}
              <div className="space-y-3 mb-8">
                {[
                  { icon: 'ri-shield-check-line', text: 'Premium European quality radiators' },
                  { icon: 'ri-truck-line', text: 'Fast delivery across Belgium & Europe' },
                  { icon: 'ri-customer-service-2-line', text: 'Expert advice & dedicated support' },
                  { icon: 'ri-price-tag-3-line', text: 'Competitive wholesale & retail pricing' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#F5F1E8] rounded-full">
                      <i className={`${item.icon} text-black text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Address block */}
              <div className="bg-[#F5F1E8] rounded-2xl px-6 py-5 mb-10">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Our Locations</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-black rounded-full mt-0.5 shrink-0">
                      <i className="ri-map-pin-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-black text-sm font-medium">🇧🇪 Belgium Office</p>
                      <p className="text-gray-600 text-sm">Leuvenselaan 467, Tienen 3300, Belgium</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-black rounded-full mt-0.5 shrink-0">
                      <i className="ri-map-pin-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-black text-sm font-medium">🇬🇧 UK Office</p>
                      <p className="text-gray-600 text-sm">London, United Kingdom</p>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/products"
                className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
              >
                Browse Our Products
                <span className="w-7 h-7 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-black text-xs"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F5F5F5] py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="mb-12">
            <span className="inline-block bg-white px-4 py-2 rounded-full text-xs font-medium text-gray-600 mb-4">
              Customer Reviews
            </span>
            <h2 className="font-sans text-5xl font-bold text-black leading-tight">
              What Our Customers<br />Are Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-black">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.username}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  "{testimonial.review}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request a Quote Section */}
      <section className="bg-white py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="bg-[#0A0A0A] rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left */}
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <span className="inline-block bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider mb-6 w-fit">
                  Bulk &amp; Commercial Orders
                </span>
                <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
                  Need a Large<br />Order? Get a<br />Custom Quote.
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-sm">
                  We offer competitive pricing for developers, contractors, and commercial projects. Tell us what you need and we'll prepare a tailored quote within 1–2 business days.
                </p>
                <div className="space-y-3 mb-10">
                  {[
                    'Volume discounts on 10+ units',
                    'Dedicated account manager',
                    'Priority dispatch & logistics',
                    'CE certified, warranted products',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm text-gray-300">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/request-quote')}
                  className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-sm font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap w-fit"
                >
                  <i className="ri-file-list-3-line"></i>
                  Request a Quote
                  <span className="w-7 h-7 bg-black rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-white text-xs"></i>
                  </span>
                </button>
              </div>
              {/* Right */}
              <div className="relative hidden lg:block">
                <img
                  src="https://readdy.ai/api/search-image?query=large%20commercial%20building%20interior%20with%20multiple%20premium%20radiators%20installed%20on%20white%20walls%2C%20professional%20architectural%20photography%2C%20modern%20office%20or%20hotel%20lobby%20with%20designer%20heating%20systems%2C%20warm%20ambient%20lighting%2C%20sophisticated%20European%20interior%20with%20high-end%20radiator%20products%20displayed&width=700&height=600&seq=quotecta2&orientation=landscape"
                  alt="Commercial Radiator Installation"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: '500+', label: 'Products' },
                      { value: '1000+', label: 'Projects' },
                      { value: '10+', label: 'Years' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                        <p className="text-white text-xl font-bold">{stat.value}</p>
                        <p className="text-white/60 text-xs mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative h-[500px] flex items-center">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20radiator%20showroom%20with%20elegant%20heating%20systems%20displayed%2C%20warm%20ambient%20lighting%2C%20professional%20product%20display%20of%20designer%20radiators%20and%20towel%20warmers%2C%20contemporary%20interior%20design%2C%20premium%20heating%20products%20exhibition%2C%20sophisticated%20atmosphere&width=1920&height=500&seq=ctarad&orientation=landscape"
          alt="Premium Radiators"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/30"></div>
        
        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 flex items-center justify-between">
          <div className="text-white">
            <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-4 font-medium">PEL Partner Company — Belgium & UK</p>
            <h2 className="font-serif text-6xl font-bold mb-4 leading-tight">
              Ready to Heat<br />Your Space?
            </h2>
            <p className="text-white/80 text-base font-light mb-8 max-w-sm">
              Browse our full range of premium radiators and find the perfect fit for your home or project.
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-sm font-medium hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              View All Radiators
              <span className="w-7 h-7 bg-black rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-xs"></i>
              </span>
            </Link>
          </div>
          
          <div className="text-white text-right max-w-sm hidden md:block">
            <div className="space-y-4">
              {[
                { icon: 'ri-phone-line', label: '+32487261970' },
                { icon: 'ri-mail-line', label: 'pelpartnercompany@gmail.com' },
                { icon: 'ri-map-pin-line', label: 'Tienen, Belgium & London, UK' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-end gap-3">
                  <span className="text-white/80 text-sm">{item.label}</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full shrink-0">
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
